import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Plus, Search, Edit2, Trash2, Save, X, ChevronDown, 
  Copy, Users, Check, Layers, Play, ChevronRight, FileText, Filter
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';

const DAY_NAMES = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const DAY_COLORS = [
  'bg-blue-500/20 border-blue-500/30 text-blue-400',
  'bg-purple-500/20 border-purple-500/30 text-purple-400',
  'bg-green-500/20 border-green-500/30 text-green-400',
  'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
  'bg-pink-500/20 border-pink-500/30 text-pink-400',
  'bg-cyan-500/20 border-cyan-500/30 text-cyan-400',
  'bg-orange-500/20 border-orange-500/30 text-orange-400',
];

// Card do template semanal
const TemplateCard = ({ template, onEdit, onDelete, onView, onDuplicate, onApply }) => {
  const activeDays = template.days?.filter(d => d.enabled)?.length || 0;
  
  return (
    <motion.div
      className="bg-zinc-950/50 border border-white/5 rounded-xl p-4 hover:border-neon-lime/30 transition-all"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="weekly-template-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-bold text-white text-lg">{template.name}</h4>
          {template.category && (
            <span className="inline-block text-xs bg-neon-lime/20 text-neon-lime px-2 py-0.5 rounded-full mt-1">
              {template.category}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={onDuplicate} className="text-zinc-400 hover:text-neon-lime p-2" title="Duplicar">
            <Copy className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit} className="text-zinc-400 hover:text-neon-purple p-2" title="Editar">
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} className="text-zinc-400 hover:text-red-500 p-2" title="Excluir">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {template.description && (
        <p className="text-zinc-500 text-sm mb-3 line-clamp-2">{template.description}</p>
      )}
      
      {/* Mini calendário visual */}
      <div className="flex gap-1 mb-3">
        {DAY_NAMES.map((dayName, idx) => {
          const dayConfig = template.days?.find(d => d.day_of_week === idx);
          const isActive = dayConfig?.enabled && dayConfig?.saved_workout_id;
          return (
            <div
              key={dayName}
              className={`
                flex-1 h-8 rounded flex items-center justify-center text-xs font-bold
                ${isActive 
                  ? DAY_COLORS[idx] + ' border' 
                  : 'bg-zinc-800/50 text-zinc-600'
                }
              `}
              title={`${dayName}${dayConfig?.saved_workout_name ? `: ${dayConfig.saved_workout_name}` : ''}`}
            >
              {dayName.charAt(0)}
            </div>
          );
        })}
      </div>
      
      <div className="flex items-center justify-between text-sm text-zinc-400 mb-3">
        <span className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          {activeDays} dias ativos
        </span>
      </div>
      
      <div className="flex gap-2">
        <Button onClick={onView} variant="outline" className="flex-1 border-neon-lime/30 text-neon-lime hover:bg-neon-lime/10 text-sm">
          Ver Detalhes
        </Button>
        <Button onClick={onApply} className="flex-1 bg-neon-lime text-black font-bold text-sm hover:bg-neon-lime/80">
          <Users className="w-4 h-4 mr-1" /> Aplicar
        </Button>
      </div>
    </motion.div>
  );
};

// Seletor de dia da semana no formulário com autocomplete
const DaySelector = ({ day, savedWorkouts, onChange, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputMode, setInputMode] = useState(day.saved_workout_id ? 'select' : 'input'); // 'select' ou 'input'
  const [manualName, setManualName] = useState(day.custom_name || '');
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const handleModeChange = (mode) => {
    setInputMode(mode);
    if (mode === 'input') {
      // Limpar seleção de treino salvo quando mudar para input manual
      onChange({
        ...day,
        saved_workout_id: null,
        saved_workout_name: null
      });
    }
    setSearchResults([]);
    setShowSuggestions(false);
  };
  
  const handleManualNameChange = (value) => {
    setManualName(value);
    
    // Buscar treinos que correspondem ao texto digitado
    if (value.trim().length >= 2) {
      const filtered = savedWorkouts.filter(w => 
        w.name.toLowerCase().includes(value.toLowerCase())
      );
      setSearchResults(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSearchResults([]);
      setShowSuggestions(false);
    }
    
    // Atualizar o dia com nome customizado
    onChange({
      ...day,
      custom_name: value || null,
      saved_workout_id: null,
      saved_workout_name: null
    });
  };
  
  const handleSelectSuggestion = (workout) => {
    setManualName(workout.name);
    setShowSuggestions(false);
    setSearchResults([]);
    
    // Vincular o treino salvo ao dia
    onChange({
      ...day,
      saved_workout_id: workout.id,
      saved_workout_name: workout.name,
      custom_name: null
    });
  };
  
  const handleWorkoutSelect = (workoutId) => {
    const workout = savedWorkouts.find(w => w.id === workoutId);
    onChange({
      ...day,
      saved_workout_id: workoutId || null,
      saved_workout_name: workout?.name || null,
      custom_name: null // Limpar nome manual quando selecionar treino
    });
    setManualName('');
  };
  
  return (
    <div className={`border rounded-lg p-3 ${day.enabled ? DAY_COLORS[day.day_of_week] : 'bg-zinc-900 border-zinc-700'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onChange({ ...day, enabled: !day.enabled })}
            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors
              ${day.enabled ? 'bg-neon-lime border-neon-lime text-black' : 'border-zinc-600'}`}
          >
            {day.enabled && <Check className="w-4 h-4" />}
          </button>
          <span className={`font-bold ${day.enabled ? 'text-white' : 'text-zinc-500'}`}>
            {day.day_name || DAY_NAMES[day.day_of_week]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-zinc-400 hover:text-white p-1"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      
      {day.enabled && (
        <div className="mt-3 space-y-2">
          {/* Toggle entre digitar nome ou selecionar treino salvo */}
          <div className="flex gap-1 mb-2">
            <button
              type="button"
              onClick={() => handleModeChange('input')}
              className={`flex-1 text-xs py-1.5 px-2 rounded-l-lg transition-colors ${
                inputMode === 'input' 
                  ? 'bg-neon-lime text-black font-bold' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              Digitar Nome
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('select')}
              className={`flex-1 text-xs py-1.5 px-2 rounded-r-lg transition-colors ${
                inputMode === 'select' 
                  ? 'bg-neon-lime text-black font-bold' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              Treino Salvo
            </button>
          </div>
          
          {inputMode === 'input' ? (
            <div className="relative">
              <label className="block text-xs text-zinc-400 mb-1">
                Nome do Treino <span className="text-zinc-600">(digite para buscar)</span>
              </label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={manualName}
                  onChange={(e) => handleManualNameChange(e.target.value)}
                  onFocus={() => {
                    if (searchResults.length > 0) setShowSuggestions(true);
                  }}
                  onBlur={() => {
                    // Delay para permitir clique nas sugestões
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  placeholder="Ex: Treino A - Peito e Tríceps"
                  className="bg-zinc-800 border-zinc-700 text-white text-sm h-9 pl-8"
                  data-testid="workout-name-autocomplete"
                />
              </div>
              
              {/* Lista de sugestões (autocomplete) */}
              {showSuggestions && searchResults.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                  <div className="p-1.5 border-b border-zinc-700">
                    <span className="text-xs text-zinc-500">
                      {searchResults.length} treino(s) encontrado(s)
                    </span>
                  </div>
                  {searchResults.map((workout) => (
                    <button
                      key={workout.id}
                      type="button"
                      onClick={() => handleSelectSuggestion(workout)}
                      className="w-full text-left px-3 py-2 hover:bg-neon-lime/20 transition-colors flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4 text-neon-lime" />
                      <div>
                        <p className="text-white text-sm font-medium">{workout.name}</p>
                        {workout.category && (
                          <p className="text-xs text-zinc-500">{workout.category} • {workout.exercises?.length || 0} exercícios</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Indicador de treino vinculado */}
              {day.saved_workout_id && (
                <p className="text-xs text-neon-lime mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Treino vinculado: {day.saved_workout_name}
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Selecionar Treino Salvo</label>
              <select
                value={day.saved_workout_id || ''}
                onChange={(e) => handleWorkoutSelect(e.target.value)}
                className="w-full h-9 px-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
              >
                <option value="">Selecione um treino...</option>
                {savedWorkouts.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Mostrar treino selecionado ou nome digitado (apenas no modo select) */}
          {inputMode === 'select' && (day.saved_workout_name || day.custom_name) && (
            <p className="text-xs text-white bg-zinc-800/50 px-2 py-1 rounded">
              <FileText className="w-3 h-3 inline mr-1" />
              {day.saved_workout_name || day.custom_name}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Modal de detalhes do template
const TemplateDetailsModal = ({ template, onClose, onEdit, onApply }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
    <motion.div 
      className="bg-zinc-900 border border-white/10 rounded-2xl p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto" 
      initial={{ opacity: 0, scale: 0.9 }} 
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-2xl text-white">{template.name}</h3>
          {template.category && (
            <span className="inline-block text-xs bg-neon-lime/20 text-neon-lime px-2 py-0.5 rounded-full mt-1">
              {template.category}
            </span>
          )}
        </div>
        <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white">
          <X className="w-5 h-5" />
        </Button>
      </div>
      
      {template.description && (
        <p className="text-zinc-400 mb-6">{template.description}</p>
      )}
      
      <h4 className="text-sm font-medium text-zinc-400 uppercase mb-4">Programação Semanal</h4>
      
      <div className="space-y-3">
        {template.days?.map((day, idx) => (
          <div 
            key={idx}
            className={`border rounded-lg p-4 ${
              day.enabled 
                ? DAY_COLORS[day.day_of_week] + ' border' 
                : 'bg-zinc-950 border-zinc-800 opacity-50'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-bold text-white">{day.day_name}</span>
                {day.custom_name && (
                  <span className="text-sm text-zinc-400">• {day.custom_name}</span>
                )}
              </div>
              {!day.enabled && (
                <span className="text-xs text-zinc-500">Desativado</span>
              )}
            </div>
            
            {day.enabled && day.saved_workout_name && (
              <div className="mt-2">
                <div className="flex items-center gap-2 text-sm text-white mb-2">
                  <FileText className="w-4 h-4 text-neon-lime" />
                  <span className="font-medium">{day.saved_workout_name}</span>
                </div>
                
                {day.workout_details?.exercises && (
                  <div className="space-y-1 ml-6">
                    {day.workout_details.exercises.slice(0, 5).map((ex, exIdx) => (
                      <div key={ex.id || `${ex.name}-${exIdx}`} className="flex items-center gap-2 text-xs text-zinc-400">
                        <ChevronRight className="w-3 h-3" />
                        <span>{ex.name}</span>
                        {ex.execution_details && (
                          <span className="text-zinc-500">({ex.execution_details})</span>
                        )}
                      </div>
                    ))}
                    {day.workout_details.exercises.length > 5 && (
                      <p className="text-xs text-zinc-500 ml-5">
                        +{day.workout_details.exercises.length - 5} mais exercícios...
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {day.enabled && !day.saved_workout_name && (
              <p className="text-sm text-zinc-500 italic">Nenhum treino vinculado</p>
            )}
          </div>
        ))}
      </div>
      
      <div className="flex gap-3 mt-6">
        <Button onClick={onEdit} className="flex-1 bg-neon-purple text-white font-bold">
          <Edit2 className="w-5 h-5 mr-2" />Editar
        </Button>
        <Button onClick={onApply} className="flex-1 bg-neon-lime text-black font-bold">
          <Users className="w-5 h-5 mr-2" />Aplicar a Aluno
        </Button>
        <Button onClick={onClose} className="bg-zinc-800 text-white">Fechar</Button>
      </div>
    </motion.div>
  </div>
);

// Modal de aplicação do template
const ApplyTemplateModal = ({ template, users, onClose, onApply }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  
  const filteredUsers = users.filter(u => 
    u.role !== 'admin' && 
    (u.name?.toLowerCase().includes(searchUser.toLowerCase()) || 
     u.email?.toLowerCase().includes(searchUser.toLowerCase()))
  );
  
  const handleApply = async () => {
    if (!selectedUserId) {
      toast.error('Selecione um aluno');
      return;
    }
    
    setApplying(true);
    try {
      await onApply({
        userId: selectedUserId,
        startDate,
        endDate: endDate || null,
        replaceExisting
      });
    } finally {
      setApplying(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div 
        className="bg-zinc-900 border border-neon-lime/30 rounded-2xl p-6 max-w-lg w-full" 
        initial={{ opacity: 0, scale: 0.9 }} 
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-neon-lime" />
            <h3 className="font-bold text-xl text-white">Aplicar Template</h3>
          </div>
          <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="mb-4 p-3 bg-zinc-950 rounded-lg border border-zinc-800">
          <p className="text-sm text-zinc-400">Template:</p>
          <p className="font-bold text-white">{template.name}</p>
          <p className="text-xs text-zinc-500">
            {template.days?.filter(d => d.enabled).length} dias de treino
          </p>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Selecionar Aluno *</label>
            <Input
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              placeholder="Buscar por nome ou email..."
              className="bg-zinc-800 border-zinc-700 text-white mb-2"
            />
            <div className="max-h-40 overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-lg">
              {filteredUsers.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-4">Nenhum aluno encontrado</p>
              ) : (
                filteredUsers.map(user => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => setSelectedUserId(user.id)}
                    className={`w-full flex items-center gap-3 p-3 text-left hover:bg-zinc-800 transition-colors
                      ${selectedUserId === user.id ? 'bg-neon-lime/10 border-l-2 border-neon-lime' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-white text-sm font-bold">
                      {user.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{user.name}</p>
                      <p className="text-zinc-500 text-xs">{user.email}</p>
                    </div>
                    {selectedUserId === user.id && (
                      <Check className="w-4 h-4 text-neon-lime ml-auto" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Data Início *</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Data Fim (opcional)</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>
          
          <label className="flex items-center gap-3 cursor-pointer">
            <button
              type="button"
              onClick={() => setReplaceExisting(!replaceExisting)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                ${replaceExisting ? 'bg-orange-500 border-orange-500 text-white' : 'border-zinc-600'}`}
            >
              {replaceExisting && <Check className="w-3 h-3" />}
            </button>
            <span className="text-zinc-300 text-sm">Substituir treinos existentes</span>
          </label>
          {replaceExisting && (
            <p className="text-xs text-orange-400 ml-8">
              ⚠️ Todos os treinos atuais do aluno serão removidos
            </p>
          )}
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button 
            onClick={handleApply}
            disabled={!selectedUserId || applying}
            className="flex-1 bg-neon-lime text-black font-bold disabled:opacity-50"
          >
            {applying ? 'Aplicando...' : 'Aplicar Template'}
          </Button>
          <Button onClick={onClose} className="bg-zinc-800 text-white">Cancelar</Button>
        </div>
      </motion.div>
    </div>
  );
};

// Componente principal
export const WeeklyTemplatesManager = ({ savedWorkouts = [], users = [], onRefresh }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [applyingTemplate, setApplyingTemplate] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    days: DAY_NAMES.map((name, idx) => ({
      day_of_week: idx,
      day_name: name,
      saved_workout_id: null,
      saved_workout_name: null,
      custom_name: null,
      enabled: idx < 5 // Segunda a Sexta ativados por padrão
    }))
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const [templatesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/admin/weekly-templates`),
        axios.get(`${API}/admin/weekly-templates/categories/list`).catch(() => ({ data: [] }))
      ]);
      setTemplates(templatesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome do template é obrigatório');
      return;
    }
    
    // Aceitar dias que tenham treino salvo OU nome customizado
    const activeDays = formData.days.filter(d => d.enabled && (d.saved_workout_id || d.custom_name));
    if (activeDays.length === 0) {
      toast.error('Adicione pelo menos um treino a um dia da semana');
      return;
    }
    
    try {
      if (editingId) {
        await axios.patch(`${API}/admin/weekly-templates/${editingId}`, formData);
        toast.success('Template atualizado!');
      } else {
        await axios.post(`${API}/admin/weekly-templates`, formData);
        toast.success('Template criado com sucesso!');
      }
      resetForm();
      fetchTemplates();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Erro ao salvar template');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Excluir o template "${name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/weekly-templates/${id}`);
      toast.success('Template excluído');
      fetchTemplates();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/admin/weekly-templates/${id}/duplicate`);
      toast.success('Template duplicado!');
      fetchTemplates();
    } catch (error) {
      toast.error('Erro ao duplicar');
    }
  };

  const handleApplyTemplate = async ({ userId, startDate, endDate, replaceExisting }) => {
    try {
      const response = await axios.post(
        `${API}/admin/weekly-templates/${applyingTemplate.id}/apply/${userId}`,
        {
          start_date: startDate,
          end_date: endDate,
          replace_existing: replaceExisting
        }
      );
      toast.success(response.data.message || 'Template aplicado com sucesso!');
      setApplyingTemplate(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aplicar template');
    }
  };

  const handleViewTemplate = async (template) => {
    try {
      const response = await axios.get(`${API}/admin/weekly-templates/${template.id}`);
      setViewingTemplate(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      days: DAY_NAMES.map((name, idx) => ({
        day_of_week: idx,
        day_name: name,
        saved_workout_id: null,
        saved_workout_name: null,
        custom_name: null,
        enabled: idx < 5
      }))
    });
  };

  const openEdit = async (template) => {
    try {
      const response = await axios.get(`${API}/admin/weekly-templates/${template.id}`);
      const fullTemplate = response.data;
      
      // Garantir que todos os 7 dias estejam presentes
      const existingDays = fullTemplate.days || [];
      const days = DAY_NAMES.map((name, idx) => {
        const existing = existingDays.find(d => d.day_of_week === idx);
        return existing || {
          day_of_week: idx,
          day_name: name,
          saved_workout_id: null,
          saved_workout_name: null,
          custom_name: null,
          enabled: false
        };
      });
      
      setFormData({
        name: fullTemplate.name,
        description: fullTemplate.description || '',
        category: fullTemplate.category || '',
        days
      });
      setEditingId(fullTemplate.id);
      setShowForm(true);
      setViewingTemplate(null);
    } catch (error) {
      toast.error('Erro ao carregar template');
    }
  };

  const updateDay = (dayIndex, updatedDay) => {
    setFormData(prev => ({
      ...prev,
      days: prev.days.map((d, idx) => idx === dayIndex ? updatedDay : d)
    }));
  };

  const filteredTemplates = templates.filter(t => {
    const matchSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = !categoryFilter || t.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  return (
    <div className="bg-zinc-900/50 border border-neon-lime/30 rounded-2xl p-6" data-testid="weekly-templates-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Calendar className="w-8 h-8 text-neon-lime" />
          <div>
            <h3 className="font-primary font-bold text-xl text-white">Treinos de Referência</h3>
            <p className="text-zinc-500 text-sm">Templates semanais completos para reutilização</p>
          </div>
        </div>
        <Button 
          onClick={() => { setShowForm(true); setEditingId(null); resetForm(); setShowForm(true); }} 
          className="bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80"
          data-testid="new-template-button"
        >
          <Plus className="w-5 h-5 mr-2" />Novo Template
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input 
            type="text" 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            placeholder="Buscar template (ex: M1AVA4)..." 
            className="pl-10 bg-zinc-950/50 border-zinc-800 text-white h-10" 
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <select 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)} 
            className="pl-10 pr-8 h-10 bg-zinc-950/50 border border-zinc-800 rounded-lg text-white min-w-[180px]"
          >
            <option value="">Todas categorias</option>
            {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        </div>
      </div>

      {/* Formulário */}
      <AnimatePresence>
        {showForm && (
          <motion.div 
            className="bg-zinc-950/50 border border-neon-lime/30 rounded-xl p-5 mb-4" 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <h4 className="font-bold text-white text-lg mb-4">
              {editingId ? 'Editar Template' : 'Novo Template Semanal'}
            </h4>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Nome do Template *</label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} 
                    placeholder="Ex: M1AVA4, Iniciante Full Body" 
                    className="bg-zinc-900 border-zinc-700 text-white" 
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Categoria</label>
                  <Input 
                    value={formData.category} 
                    onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))} 
                    placeholder="Ex: Iniciante, Avançado, Hipertrofia" 
                    className="bg-zinc-900 border-zinc-700 text-white" 
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Descrição</label>
                <textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} 
                  placeholder="Descrição do template..." 
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-lg p-3 min-h-[60px]" 
                />
              </div>

              {/* Dias da semana */}
              <div>
                <label className="block text-sm text-zinc-400 mb-3">
                  Programação Semanal 
                  <span className="text-xs text-zinc-600 ml-2">
                    (Selecione os dias e vincule treinos salvos)
                  </span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {formData.days.map((day, idx) => (
                    <DaySelector
                      key={day.day_of_week}
                      day={day}
                      savedWorkouts={savedWorkouts}
                      onChange={(updatedDay) => updateDay(idx, updatedDay)}
                    />
                  ))}
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={handleSave}
                  className="flex-1 bg-neon-lime text-black font-bold"
                  data-testid="save-template-button"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {editingId ? 'Salvar Alterações' : 'Criar Template'}
                </Button>
                <Button onClick={resetForm} className="bg-zinc-800 text-white">
                  Cancelar
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lista de templates */}
      {loading ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-2 border-neon-lime border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-zinc-500">Carregando templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">
            {templates.length === 0 ? 'Nenhum template criado ainda' : 'Nenhum template encontrado'}
          </p>
          <p className="text-zinc-600 text-sm mt-1">
            Crie templates semanais para aplicar rapidamente em seus alunos
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((t) => (
            <TemplateCard 
              key={t.id} 
              template={t} 
              onEdit={() => openEdit(t)} 
              onDelete={() => handleDelete(t.id, t.name)} 
              onView={() => handleViewTemplate(t)}
              onDuplicate={() => handleDuplicate(t.id)}
              onApply={() => setApplyingTemplate(t)}
            />
          ))}
        </div>
      )}

      {/* Modal de detalhes */}
      {viewingTemplate && (
        <TemplateDetailsModal
          template={viewingTemplate}
          onClose={() => setViewingTemplate(null)}
          onEdit={() => openEdit(viewingTemplate)}
          onApply={() => {
            setApplyingTemplate(viewingTemplate);
            setViewingTemplate(null);
          }}
        />
      )}

      {/* Modal de aplicação */}
      {applyingTemplate && (
        <ApplyTemplateModal
          template={applyingTemplate}
          users={users}
          onClose={() => setApplyingTemplate(null)}
          onApply={handleApplyTemplate}
        />
      )}
    </div>
  );
};
